import { GraphQLResult } from "@aws-amplify/api-graphql";
import { API, graphqlOperation } from "aws-amplify";
import * as formik from "formik";
import { useField } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { FormRadio, radioDataType } from "../FormRadio/FormRadio";
import { Modal } from "../Modal/Modal";
import {
  convertDateStrings,
  handleHourMismatch,
  notificationSchema,
} from "../NewPushNotification/utils";
import { Notification } from "../Notification/Notification";
import { ToastContent } from "../Notification/types";
import {
  NavProps,
  PushNotificationType,
  UpdatePushNotificationResponse,
  getPushNotificationById,
} from "../_shared/pushNotifications/types";
import "./PushNotificationEdit.css";
import { isDuePastOrWithinHour } from "./internals";
import Table from "react-bootstrap/Table";

export const PushNotificationEdit = () => {
  const [notification, setNotification] = useState({} as PushNotificationType);
  const { Formik } = formik;
  const location = useLocation().state as NavProps;
  const [tmcUsersInfo, setTmcUsersInfo] = useState("");
  const [tiUsersInfo, setTiUsersInfo] = useState("");
  const [deviceOSInfo, setDeviceOSInfo] = useState(
    "android" || "ios" || "both"
  );

  const [toast, setToast] = useState<ToastContent>({
    title: "",
    description: "",
    isVisible: false,
    notificationVariant: "success",
  });

  const [showStopModal, setShowStopModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOneHourModal, setShowOneHourModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [pushNotificationLoaded, setPushNotificationLoaded] =
    useState<boolean>(false);

  const [isEditDisabled, setIsEditDisabled] = useState<boolean>(false);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState<boolean>(false);
  const [disableStopBtn, setDisableStopBtn] = useState<boolean>(false);
  const [statusOverview, setStatusOverview] = useState<string>("");

  const { id } = useParams();
  const navigate = useNavigate();

  const getPushNotificationById = useCallback(async () => {
    try {
      const pushNotificationById = (await API.graphql(
        graphqlOperation(queries.getPushNotificationById, {
          pushNotificationId: id,
        })
      )) as GraphQLResult<getPushNotificationById>;
      const pushNotificationData =
        pushNotificationById?.data?.getPushNotificationById;

      console.log("pushNotificationData: ", pushNotificationById);

      setNotification({
        ...pushNotificationData,
        dueOn: handleHourMismatch(pushNotificationData.dueOn),
      });

      setTmcUsersInfo(pushNotificationData.tmcUsers);
      setTiUsersInfo(pushNotificationData.tiUsers);
      setDeviceOSInfo(pushNotificationData.deviceOS);

      if (
        location.status === "STOP" ||
        location.status === "COMPLETED" ||
        isDuePastOrWithinHour(pushNotificationData.dueOn)
      ) {
        setIsEditDisabled(true);
        setStatusOverview("Push notification can no longer be edited");
      }

      setDisableStopBtn(
        location.status === "COMPLETED" || location.status === "STOP"
      );

      setPushNotificationLoaded(true);
    } catch (e) {
      console.log("getPushNotificationById error: ", e);
      setShowErrorModal(true);
    }
  }, [id]);

  const DatePickerField = (props: formik.FieldHookConfig<DatePicker>) => {
    const [field, meta, helpers] = useField(props);
    return (
      <DatePicker
        selected={field.value && new Date(field.value)}
        onChange={(value) => helpers.setValue(value)}
        showMonthPicker
        showTimeInput
        showMonthDropdown
        showYearDropdown
        timeInputLabel="Time:"
        dateFormat="dd/MM/yyyy h:mm aa"
        className="datePicker"
        name="dueOn"
        disabled={isEditDisabled}
      />
    );
  };

  enum toastContent {
    "SUCCESS" = "You successfully updated the push notification",
    "ERROR" = "Failed to update the push notification",
  }

  const handleShowToast = (isSuccess: boolean) => {
    setToast({
      title: isSuccess ? "Success!" : "Error!",
      description: isSuccess ? toastContent.SUCCESS : toastContent.ERROR,
      isVisible: true,
      notificationVariant: isSuccess ? "success" : "danger",
    });

    if (isSuccess) {
      setTimeout(() => {
        navigate(-1);
      }, 3000);
    }
  };

  const handleUpdateNotification = async (values: PushNotificationType) => {
    console.log("values: ", values);

    if (isDuePastOrWithinHour(handleHourMismatch(notification.dueOn, -1))) {
      setIsEditDisabled(true);
      setStatusOverview("Push notification can no longer be edited");
      setShowOneHourModal(true);
      return false;
    }

    const time = convertDateStrings(values.dueOn);
    const pushNotification: PushNotificationType = {
      ...values,
      scheduleId: notification.scheduleId,
      dueOn: time,
      tiUsers: tiUsersInfo,
      tmcUsers: tmcUsersInfo,
      deviceOS: deviceOSInfo,
    };

    try {
      const response = (await API.graphql(
        graphqlOperation(mutations.updatePushNotification, {
          pushNotification: pushNotification,
        })
      )) as GraphQLResult<UpdatePushNotificationResponse>;
      if (response.data.updatePushNotification) {
        console.log("update succeded");
        handleShowToast(true);
        return true;
      } else {
        console.log("update failed");
        handleShowToast(false);
        throw new Error("Something went wrong");
      }
    } catch (e) {
      console.log("handleUpdateNotification error: " + JSON.stringify(e));
      setShowErrorModal(true);
    }
  };

  const goBack = () => {
    if (showErrorModal) {
      setShowErrorModal(false);
    }
    navigate(-1);
  };

  const handleNotificationClose = () => {
    setToast({ ...toast, isVisible: false });
  };

  const stopPushNotification = async () => {
    setDisableSubmitBtn(true);
    setDisableStopBtn(true);
    try {
      const response = (await API.graphql(
        graphqlOperation(mutations.updatePushNotification, {
          pushNotification: {
            ...notification,
            dueOn: convertDateStrings(notification.dueOn),
            status: "STOP",
          },
        })
      )) as GraphQLResult<UpdatePushNotificationResponse>;

      handleCloseStopModal();
      if (response.data.updatePushNotification) {
        console.log("succeeded");
        handleShowToast(true);
      } else {
        console.log("failed");
        handleShowToast(false);
        setDisableSubmitBtn(isEditDisabled);
        setDisableStopBtn(false);
      }
    } catch (e) {
      console.log("stopPushNotification error: " + JSON.stringify(e));
      setShowErrorModal(true);
      setDisableSubmitBtn(isEditDisabled);
      setDisableStopBtn(false);
    }
  };

  const radioTMC: radioDataType[] = [
    { radioLabel: "Yes", radioId: "yes", radioName: "tmcUsers" },
    { radioLabel: "N/A", radioId: "n/a", radioName: "tmcUsers" },
    { radioLabel: "No", radioId: "no", radioName: "tmcUsers" },
  ];

  const radioTI: radioDataType[] = [
    { radioLabel: "Yes", radioId: "yes", radioName: "tiUsers" },
    { radioLabel: "N/A", radioId: "n/a", radioName: "tiUsers" },
    { radioLabel: "No", radioId: "no", radioName: "tiUsers" },
  ];
  const radioDevice: radioDataType[] = [
    { radioLabel: "Both", radioId: "both", radioName: "deviceOS" },
    { radioLabel: "IOS Only", radioId: "ios", radioName: "deviceOS" },
    {
      radioLabel: "Android Only",
      radioId: "android",
      radioName: "deviceOS",
    },
  ];

  const handleOpenStopModal = () => {
    setShowStopModal(true);
  };

  const handleCloseStopModal = () => {
    setShowStopModal(false);
  };

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleCloseOneHourModal = () => {
    setShowOneHourModal(false);
  };

  useEffect(() => {
    getPushNotificationById();
  }, [getPushNotificationById]);

  return (
    <Container fluid as={"div"} className="screenContainer">
      <Row>
        <Col xs={1} />
        <Col>
          <h1 className="editTitle">
            Edit push notification: {notification.scheduleId}
          </h1>
        </Col>
      </Row>
      <Row>
        <Col xs={1} />
        {pushNotificationLoaded && (
          <Table bordered striped size="sm" style={{ width: "50%" }}>
            <tbody>
              <tr>
                <td colSpan={6}>Schedule Id:</td>
                <td colSpan={6}>{notification.scheduleId}</td>
              </tr>
              <tr>
                <td colSpan={6}>Successful notifications:</td>
                <td colSpan={6}>{location.success}</td>
              </tr>
              <tr>
                <td colSpan={6}>Failed notifications</td>
                <td colSpan={6}>{location.failure}</td>
              </tr>
              <tr>
                <td colSpan={6}>Users opted out:</td>
                <td colSpan={6}>{location.optedOut}</td>
              </tr>
              <tr>
                <td colSpan={6}>Status:</td>
                <td colSpan={6}>{location.status}</td>
              </tr>
              {statusOverview && (
                <tr>
                  <td colSpan={6}>Status overview:</td>
                  <td colSpan={6}>{statusOverview}</td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Row>
      {pushNotificationLoaded && (
        <Formik
          validationSchema={notificationSchema}
          initialValues={notification}
          onSubmit={async (
            values: PushNotificationType,
            { setSubmitting, resetForm }
          ) => {
            setSubmitting(true);
            await handleUpdateNotification(values)
              .then((response) => {
                if (response === false) {
                  resetForm();
                }
                setDisableSubmitBtn(true);
              })
              .catch((e) => {
                resetForm();
              });
            setSubmitting(false);
            handleCloseEditModal();
          }}
        >
          {({
            handleChange,
            handleSubmit,
            values = notification,
            touched,
            errors,
            handleBlur,
            isSubmitting,
          }) => (
            <Form noValidate onSubmit={handleSubmit} id="PushForm">
              <div className="formContainer">
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Notification name (Internal reference)
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        name="name"
                        aria-label="Notificaton Name"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        isValid={touched.name && !errors.name}
                        isInvalid={!!errors.name}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Notification title
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        name="title"
                        aria-label="Notificaton Title"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title}
                        isValid={touched.title && !errors.title}
                        isInvalid={!!errors.title}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.title}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Notification body
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        name="body"
                        aria-label="Notificaton Body"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.body}
                        isValid={touched.body && !errors.body}
                        isInvalid={!!errors.body}
                        as={"textarea"}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.body}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Notification deeplink
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        name="deeplink"
                        aria-label="Deeplink"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.deeplink}
                        isValid={touched.deeplink && !errors.deeplink}
                        isInvalid={!!errors.deeplink}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.deeplink}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <Row
                  as={"div"}
                  className="formItem"
                  style={{ height: 48, alignItems: "center" }}
                >
                  <Col xs={3}>
                    <span>Due on (time is rounded)</span>
                  </Col>

                  <Col xs={9}>
                    <DatePickerField name="dueOn" />
                  </Col>
                </Row>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Throttle Rate (number of notifications sent per second)
                    </Form.Label>
                    <Col sm={9} style={{ margin: "auto" }}>
                      <Form.Control
                        name="throttleRate"
                        aria-label="Throttle rate"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.throttleRate}
                        isValid={touched.throttleRate && !errors.throttleRate}
                        isInvalid={!!errors.throttleRate}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.throttleRate}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row} className="checkContainer">
                    <Form.Label column sm={3}>
                      Automatically pause schedule?
                    </Form.Label>
                    <Col sm={9} style={{ margin: "auto" }}>
                      <Form.Check
                        type="switch"
                        name="autoPauseSchedule"
                        aria-label="Automatically pause schedule"
                        onChange={handleChange}
                        className="checkedInput"
                        disabled={isEditDisabled}
                      />
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Pause sending time
                    </Form.Label>
                    <Col sm={9} style={{ margin: "auto" }}>
                      <Form.Control
                        name="pauseSendingTime"
                        aria-label="Pause sending time"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.pauseSendingTime}
                        isValid={
                          touched.pauseSendingTime && !errors.pauseSendingTime
                        }
                        isInvalid={!!errors.pauseSendingTime}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.pauseSendingTime}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Resume sending time
                    </Form.Label>
                    <Col sm={9} style={{ margin: "auto" }}>
                      <Form.Control
                        name="resumeSendingTime"
                        aria-label="Resume sending time"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.resumeSendingTime}
                        isValid={
                          touched.resumeSendingTime && !errors.resumeSendingTime
                        }
                        isInvalid={!!errors.resumeSendingTime}
                        disabled={isEditDisabled}
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.resumeSendingTime}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <Form.Group as={Row}>
                    <Form.Label column sm={3}>
                      Application
                    </Form.Label>
                    <Col sm={9} style={{ margin: "auto" }}>
                      <Form.Control
                        name="application"
                        aria-label="application"
                        size="lg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.application}
                        isValid={touched.application && !errors.application}
                        isInvalid={!!errors.application}
                        disabled
                      ></Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.application}
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                </div>
                <div className="formItem">
                  <FormRadio
                    radioData={radioTMC}
                    labelText="TMC users"
                    setInfo={setTmcUsersInfo}
                    defaultValue={tmcUsersInfo}
                    disabled={isEditDisabled}
                  ></FormRadio>
                </div>
                <div className="formItem">
                  <FormRadio
                    radioData={radioTI}
                    labelText="TI users"
                    setInfo={setTiUsersInfo}
                    defaultValue={tiUsersInfo}
                    disabled={isEditDisabled}
                  ></FormRadio>
                </div>
                <div className="formItem">
                  <FormRadio
                    radioData={radioDevice}
                    labelText="Device OS"
                    setInfo={setDeviceOSInfo}
                    defaultValue={deviceOSInfo}
                    disabled={isEditDisabled}
                  ></FormRadio>
                </div>
                <div className="formItem">
                  <Button
                    type="button"
                    className="btn btn-danger save-button"
                    disabled={
                      isEditDisabled || isSubmitting || disableSubmitBtn
                    }
                    onClick={() => handleOpenEditModal()}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-warning save-button"
                    disabled={disableStopBtn}
                    onClick={() => handleOpenStopModal()}
                  >
                    Stop
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-secondary"
                    onClick={goBack}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
      <div className="notificationContainer">
        <Notification
          show={toast.isVisible}
          timer={4000}
          position="bottom-end"
          title={toast.title}
          description={toast.description}
          autohide={true}
          handleClose={handleNotificationClose}
          variant={toast.notificationVariant}
        />
      </div>
      <Modal
        enableModal={showStopModal}
        handleClose={handleCloseStopModal}
        handleSubmit={() => stopPushNotification()}
        title={"WARNING"}
        closeButtonText="Back"
        actionButtonText="Stop push notification"
        width="halfwidth"
        disableActionButton={disableStopBtn}
      >
        Are you sure you would like to <strong>stop</strong> this push
        notification? Once it has been stopped this{" "}
        <strong>cannot be reversed</strong>.
      </Modal>
      <Modal
        enableModal={showEditModal}
        handleClose={handleCloseEditModal}
        title={"Editing notification"}
        closeButtonText="Back"
        actionButtonText="Edit"
        width="halfwidth"
        buttonType="submit"
        form="PushForm"
        disableActionButton={disableSubmitBtn}
      >
        Are you sure you would like to edit this notification?
      </Modal>
      <Modal
        enableModal={showOneHourModal}
        handleClose={handleCloseOneHourModal}
        title={"Cannot edit notification"}
        closeButtonText="Back"
        width="halfwidth"
      >
        There is less than an hour until this push notification is scheduled to
        be sent. It can no longer be edited.
      </Modal>
      <Modal
        enableModal={showErrorModal}
        handleClose={goBack}
        title={"Sorry, something went wrong"}
        closeButtonText="Back"
        width="halfwidth"
      >
        Please try again later.
      </Modal>
    </Container>
  );
};
